import React from 'react';
import { RiderDto } from '@/features/partner/supplierFee/riderToPartner/types/supplierFee.types';
import { DailyDetailDto, RiderResultDto } from '../api/promoMissionApi';
import { Mission, MissionTypeCode, PeriodCode, STATUS_LABEL } from '../types/promotion.types';
import { getMissionMeta, getPeriodName, formatPeriodDate } from '../utils/promotionHelpers';
import { formatCurrency } from '@/shared/utils/formatter';
import '../styles/MissionDetailModal.css';

interface MissionDetailModalProps {
    mission: Mission;
    today: string;
    isReadOnly: boolean;
    missionTypeCodes: MissionTypeCode[];
    periodCodes: PeriodCode[];
    riders: RiderDto[];
    riderResults: RiderResultDto[] | null;
    riderResultsLoading: boolean;
    expandedRiderIds: Set<string>;
    resultRiderMap: Map<string, string>;
    onClose: () => void;
    onEditStart: (m: Mission) => void;
    onStatusToggle: (m: Mission) => Promise<void>;
    onDelete: (seqNo: number) => Promise<void>;
    onToggleExpand: (riderId: string) => void;
}

const MissionDetailModal: React.FC<MissionDetailModalProps> = ({
                                                                   mission,
                                                                   today,
                                                                   isReadOnly,
                                                                   missionTypeCodes,
                                                                   periodCodes,
                                                                   riders,
                                                                   riderResults,
                                                                   riderResultsLoading,
                                                                   expandedRiderIds,
                                                                   resultRiderMap,
                                                                   onClose,
                                                                   onEditStart,
                                                                   onStatusToggle,
                                                                   onDelete,
                                                                   onToggleExpand,
                                                               }) => {
    const typeMeta = getMissionMeta(mission.missionType, missionTypeCodes);
    const isEnded = mission.endDate < today;

    return (
        <div className="mission-modal-overlay" onClick={onClose}>
            <div className="mission-modal" onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="mission-modal-header">
                    <div className="mission-modal-title-row">
                        <span className={`mission-type-badge badge-${mission.missionType.toLowerCase()}`}>
                            {typeMeta.name}
                        </span>
                        <h3 className="mission-modal-title">{mission.title}</h3>
                        <span className={`mission-status-badge status-${mission.status.toLowerCase()}`}>
                            {STATUS_LABEL[mission.status]}
                        </span>
                    </div>
                    <button type="button" className="mission-modal-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                {/* 본문 */}
                <div className="mission-modal-body">
                    <div className="mission-modal-grid">
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">기간</span>
                            <span className="mission-modal-value">
                                {getPeriodName(mission.period, periodCodes)}
                            </span>
                        </div>
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">목표 건수</span>
                            <span className="mission-modal-value">
                                {mission.targetCount.toLocaleString()}건
                            </span>
                        </div>
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">보상</span>
                            <span className="mission-modal-value">
                                {formatCurrency(mission.rewardAmount)}
                                {typeMeta.rewardUnit}
                            </span>
                        </div>
                        {mission.missionType !== 'FIXED' && (
                            <div className="mission-modal-item">
                                <span className="mission-modal-key">최대 인정</span>
                                <span className="mission-modal-value">
                                    {mission.maxCount === 0
                                        ? '무제한'
                                        : `${mission.maxCount.toLocaleString()}건`}
                                </span>
                            </div>
                        )}
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">시작일</span>
                            <span className="mission-modal-value">{mission.startDate}</span>
                        </div>
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">종료일</span>
                            <span className="mission-modal-value">{mission.endDate}</span>
                        </div>
                        <div className="mission-modal-item">
                            <span className="mission-modal-key">대상</span>
                            <span className="mission-modal-value">
                                {mission.targetType === 'ALL'
                                    ? '협력사 전체 라이더'
                                    : `특정 라이더 ${mission.targetRiders.length}명`}
                            </span>
                        </div>
                    </div>

                    {/* 설명 */}
                    {mission.description && (
                        <div className="mission-modal-description">
                            <span className="mission-modal-key">설명</span>
                            <p>{mission.description}</p>
                        </div>
                    )}

                    {/* 대상 라이더 목록 (SPECIFIC) */}
                    {mission.targetType === 'SPECIFIC' && (
                        <div className="mission-modal-riders">
                            <div className="mission-modal-riders-title">
                                대상 라이더
                                <span className="mission-modal-riders-count">
                                    {mission.targetRiders.length}명
                                </span>
                            </div>
                            {mission.targetRiders.length > 0 ? (
                                <div className="mission-modal-rider-list">
                                    {mission.targetRiders.map((id) => {
                                        const r = riders.find((x) => x.riderId === id);
                                        return (
                                            <span key={id} className="mission-modal-rider-chip">
                                                {r && (
                                                    <span className="mission-modal-rider-name">
                                                        {r.riderName}
                                                    </span>
                                                )}
                                                <span className="mission-modal-rider-id">{id}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="mission-modal-no-riders">지정된 라이더가 없습니다.</p>
                            )}
                        </div>
                    )}

                    {/* 라이더별 달성 현황 (종료된 미션) */}
                    {isEnded && (
                        <div className="mission-modal-result-section">
                            <div className="mission-modal-riders-title">
                                실제 지급 현황
                                {riderResults && riderResults.length > 0 && (
                                    <span className="mission-modal-riders-count">
                                        {riderResults.length}명
                                    </span>
                                )}
                            </div>
                            {riderResultsLoading ? (
                                <p className="mission-modal-result-loading">조회 중…</p>
                            ) : riderResults === null ? null : riderResults.length === 0 ? (
                                <p className="mission-modal-no-riders">
                                    정산 처리 전입니다. (주정산 업로드 완료 후 조회 가능)
                                </p>
                            ) : (
                                <div className="mission-modal-result-table-wrap">
                                    <table className="mission-modal-result-table">
                                        <colgroup>
                                            <col style={{ width: '55%' }} />
                                            <col style={{ width: '20%' }} />
                                            <col style={{ width: '25%' }} />
                                        </colgroup>
                                        <thead>
                                        <tr>
                                            <th>라이더</th>
                                            <th>배달건수</th>
                                            <th>실제 지급액</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {riderResults.map((r) => {
                                            const riderName = resultRiderMap.get(r.riderId);
                                            const totalDelivery = r.periodDetails.reduce(
                                                (sum, d) => sum + d.deliveryCount,
                                                0
                                            );
                                            const isExpanded = expandedRiderIds.has(r.riderId);
                                            return (
                                                <React.Fragment key={r.riderId}>
                                                    {/* 라이더 요약 행 */}
                                                    <tr
                                                        className="result-row-achieved"
                                                        onClick={() => onToggleExpand(r.riderId)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>
                                                            <div className="result-rider-cell">
                                                                <span className="result-expand-icon">
                                                                    {isExpanded ? '▾' : '▸'}
                                                                </span>
                                                                <span className="result-rider-label">
                                                                    {r.riderId}
                                                                    {riderName ? ` (${riderName})` : ''}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {totalDelivery > 0
                                                                ? `${totalDelivery.toLocaleString()}건`
                                                                : '—'}
                                                        </td>
                                                        <td>{r.payoutAmount.toLocaleString()}원</td>
                                                    </tr>

                                                    {/* 주정산 기간 + 일별 상세 */}
                                                    {isExpanded &&
                                                        r.periodDetails.map((d, pIdx) => (
                                                            <React.Fragment key={pIdx}>
                                                                <tr className="result-row-period">
                                                                    <td className="result-period-label">
                                                                        {formatPeriodDate(d.startDate, d.endDate)}
                                                                    </td>
                                                                    <td>
                                                                        {d.deliveryCount > 0
                                                                            ? `${d.deliveryCount.toLocaleString()}건`
                                                                            : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {mission.period === 'WEEKLY' ? (
                                                                            d.achieved ? (
                                                                                <>
                                                                                    {d.payoutAmount.toLocaleString()}원{' '}
                                                                                    <span className="result-badge result-badge-achieved">
                                                                                        달성
                                                                                    </span>
                                                                                </>
                                                                            ) : (
                                                                                <span className="result-badge result-badge-failed">
                                                                                    미달성
                                                                                </span>
                                                                            )
                                                                        ) : (
                                                                            `${d.payoutAmount.toLocaleString()}원`
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                {d.dailyDetails?.map(
                                                                    (dd: DailyDetailDto, dIdx: number) => (
                                                                        <tr key={dIdx} className="result-row-daily">
                                                                            <td className="result-daily-date">
                                                                                {`${dd.date.slice(0, 4)}-${dd.date.slice(4, 6)}-${dd.date.slice(6, 8)}`}
                                                                            </td>
                                                                            <td>
                                                                                {dd.deliveryCount.toLocaleString()}건
                                                                            </td>
                                                                            <td>
                                                                                {mission.period === 'WEEKLY'
                                                                                    ? '—'
                                                                                    : dd.payoutAmount > 0
                                                                                        ? `${dd.payoutAmount.toLocaleString()}원`
                                                                                        : dd.achieved
                                                                                            ? '달성'
                                                                                            : (
                                                                                                <span className="result-badge result-badge-failed">
                                                                                                미달성
                                                                                            </span>
                                                                                            )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* 모달 하단 버튼 */}
                {!isReadOnly && (
                    <div className="mission-modal-footer">
                        <button
                            type="button"
                            className="mission-btn mission-btn-primary"
                            onClick={() => onEditStart(mission)}
                        >
                            수정하기
                        </button>
                        {!isEnded && (
                            <button
                                type="button"
                                className={`mission-btn ${
                                    mission.status === 'ACTIVE'
                                        ? 'mission-btn-warning'
                                        : 'mission-btn-activate'
                                }`}
                                onClick={() => onStatusToggle(mission)}
                            >
                                {mission.status === 'ACTIVE' ? '비활성화' : '활성화'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="mission-btn mission-btn-danger"
                            onClick={() => onDelete(mission.seqNo)}
                        >
                            삭제
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MissionDetailModal;
